// frontend/src/components/ActivitySessions.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Computer,
  Smartphone,
  Tablet,
  Security,
  Warning,
  CheckCircle,
  Cancel,
  Refresh,
  Close as CloseIcon,
  Logout,
  Login,
  VpnKey,
  Shield,
  Search,
  Clear
} from '@mui/icons-material';
// Using built-in JavaScript Date methods instead of date-fns
import axios from '../utils/axios';

const ActivitySessions = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [error, setError] = useState('');
  
  // Sessions data (cached and filtered)
  const [allSessions, setAllSessions] = useState([]); // Full cached data
  const [sessions, setSessions] = useState([]); // Filtered display data
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [terminateConfirmOpen, setTerminateConfirmOpen] = useState(false);
  const [sessionToTerminate, setSessionToTerminate] = useState(null);
  
  // Activities data (cached and filtered)
  const [allActivities, setAllActivities] = useState([]); // Full cached data
  const [activities, setActivities] = useState([]); // Filtered display data
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityFilters, setActivityFilters] = useState({
    activity_type: '',
    risk_level: '',
    days: 30
  });
  const [activitySearch, setActivitySearch] = useState('');
  
  // Login history data (cached and filtered)
  const [allLoginHistory, setAllLoginHistory] = useState([]); // Full cached data
  const [loginHistory, setLoginHistory] = useState([]); // Filtered display data
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  const [loginPage, setLoginPage] = useState(1);
  const [loginTotal, setLoginTotal] = useState(0);
  const [loginFilters, setLoginFilters] = useState({
    login_method: '',
    success_only: null,
    days: 30
  });
  const [loginSearch, setLoginSearch] = useState('');
  
  // Sessions search
  const [sessionsSearch, setSessionsSearch] = useState('');
  
  const itemsPerPage = 20;

  // Define all callback functions first to avoid use-before-define warnings
  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setError('');
    try {
      const params = { active_only: true, limit: 100 };
      const response = await axios.get('/activity/sessions', { params });
      setAllSessions(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const loadActivities = useCallback(async () => {
    setActivitiesLoading(true);
    setError('');
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - activityFilters.days);
      
      const params = {
        limit: 500,
        start_date: startDate.toISOString()
      };
      
      if (activityFilters.activity_type) {
        params.activity_type = activityFilters.activity_type;
      }
      if (activityFilters.risk_level) {
        params.risk_level = activityFilters.risk_level;
      }
      
      const response = await axios.get('/activity/activities', { params });
      setAllActivities(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load activities');
    } finally {
      setActivitiesLoading(false);
    }
  }, [activityFilters.days, activityFilters.activity_type, activityFilters.risk_level]);

  const loadLoginHistory = useCallback(async () => {
    setLoginHistoryLoading(true);
    setError('');
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - loginFilters.days);
      
      const params = {
        limit: 500,
        start_date: startDate.toISOString()
      };
      
      if (loginFilters.login_method) {
        params.login_method = loginFilters.login_method;
      }
      if (loginFilters.success_only !== null) {
        params.success_only = loginFilters.success_only;
      }
      
      const response = await axios.get('/activity/login-history', { params });
      setAllLoginHistory(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load login history');
    } finally {
      setLoginHistoryLoading(false);
    }
  }, [loginFilters.days, loginFilters.login_method, loginFilters.success_only]);

  const filterSessions = useCallback(() => {
    if (!allSessions.length) {
      setSessions([]);
      return;
    }

    let filtered = allSessions;
    
    if (sessionsSearch.trim()) {
      const searchLower = sessionsSearch.toLowerCase();
      filtered = filtered.filter(session => 
        (session.ip_address && session.ip_address.toLowerCase().includes(searchLower)) ||
        (session.user_agent && session.user_agent.toLowerCase().includes(searchLower)) ||
        (session.device_info?.browser && session.device_info.browser.toLowerCase().includes(searchLower)) ||
        (session.device_info?.os && session.device_info.os.toLowerCase().includes(searchLower)) ||
        (session.device_info?.device_type && session.device_info.device_type.toLowerCase().includes(searchLower))
      );
    }

    setSessions(filtered);
  }, [allSessions, sessionsSearch]);

  const filterActivities = useCallback(() => {
    if (!allActivities.length) {
      setActivities([]);
      setActivityTotal(0);
      return;
    }

    let filtered = allActivities;
    
    if (activitySearch.trim()) {
      const searchLower = activitySearch.toLowerCase();
      filtered = filtered.filter(activity => 
        (activity.activity_type && activity.activity_type.toLowerCase().includes(searchLower)) ||
        (activity.description && activity.description.toLowerCase().includes(searchLower)) ||
        (activity.ip_address && activity.ip_address.toLowerCase().includes(searchLower)) ||
        (activity.error_message && activity.error_message.toLowerCase().includes(searchLower))
      );
    }

    setActivityTotal(filtered.length);
    
    const startIndex = (activityPage - 1) * itemsPerPage;
    const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);
    
    setActivities(paginatedData);
  }, [allActivities, activitySearch, activityPage]);

  const filterLoginHistory = useCallback(() => {
    if (!allLoginHistory.length) {
      setLoginHistory([]);
      setLoginTotal(0);
      return;
    }

    let filtered = allLoginHistory;
    
    if (loginSearch.trim()) {
      const searchLower = loginSearch.toLowerCase();
      filtered = filtered.filter(login => 
        (login.email && login.email.toLowerCase().includes(searchLower)) ||
        (login.login_method && login.login_method.toLowerCase().includes(searchLower)) ||
        (login.ip_address && login.ip_address.toLowerCase().includes(searchLower)) ||
        (login.user_agent && login.user_agent.toLowerCase().includes(searchLower)) ||
        (login.failure_reason && login.failure_reason.toLowerCase().includes(searchLower))
      );
    }

    setLoginTotal(filtered.length);
    
    const startIndex = (loginPage - 1) * itemsPerPage;
    const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);
    
    setLoginHistory(paginatedData);
  }, [allLoginHistory, loginSearch, loginPage]);

  useEffect(() => {
    // Load data for the current tab
    switch (currentTab) {
      case 0:
        loadSessions();
        break;
      case 1:
        loadActivities();
        break;
      case 2:
        loadLoginHistory();
        break;
      default:
        break;
    }
  }, [currentTab, loadSessions, loadActivities, loadLoginHistory]);

  // Load activities data when filters change (not search)
  useEffect(() => {
    if (currentTab === 1) {
      loadActivities();
    }
  }, [currentTab, activityFilters, loadActivities]);

  // Load login history when filters change (not search)
  useEffect(() => {
    if (currentTab === 2) {
      loadLoginHistory();
    }
  }, [currentTab, loginFilters, loadLoginHistory]);

  // Client-side filtering for sessions
  useEffect(() => {
    filterSessions();
  }, [filterSessions]);

  // Client-side filtering for activities
  useEffect(() => {
    filterActivities();
  }, [filterActivities]);

  // Client-side filtering for login history
  useEffect(() => {
    filterLoginHistory();
  }, [filterLoginHistory]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setError('');
  };

  const handleTerminateSession = (session) => {
    setSessionToTerminate(session);
    setTerminateConfirmOpen(true);
  };

  const confirmTerminateSession = async () => {
    if (!sessionToTerminate) return;
    
    try {
      await axios.delete(`/activity/sessions/${sessionToTerminate.session_id}`);
      setTerminateConfirmOpen(false);
      setSessionToTerminate(null);
      loadSessions(); // Reload sessions to refresh cache
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to terminate session');
    }
  };

  const handleTerminateAllSessions = async () => {
    try {
      await axios.delete('/activity/sessions/all', {
        params: { exclude_current: true }
      });
      loadSessions(); // Reload sessions to refresh cache
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to terminate sessions');
    }
  };

  const getDeviceIcon = (deviceInfo) => {
    if (!deviceInfo || !deviceInfo.device_type) return <Computer />;
    
    switch (deviceInfo.device_type.toLowerCase()) {
      case 'mobile':
        return <Smartphone />;
      case 'tablet':
        return <Tablet />;
      default:
        return <Computer />;
    }
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'login':
        return <Login color="success" />;
      case 'logout':
        return <Logout color="action" />;
      case 'login_failed':
        return <Cancel color="error" />;
      case 'mfa_setup':
      case 'mfa_verify':
      case 'mfa_disable':
        return <VpnKey color="primary" />;
      case 'password_change':
      case 'password_set':
        return <Security color="warning" />;
      default:
        return <Shield color="action" />;
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
      default:
        return 'success';
    }
  };

  const formatRelativeTime = (timestamp) => {
    // Use the SAME method as backend: new Date() for current UTC time
    // Backend uses: datetime.utcnow() which is equivalent to new Date() in JS
    const now = new Date(); // This is current UTC time in JS (same as Python's datetime.utcnow())
    
    // FIX: Add Z suffix if missing to force UTC parsing
    let utcTimestamp = timestamp;
    if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+')) {
      utcTimestamp = timestamp + 'Z';
    }
    const utcTime = new Date(utcTimestamp);
    
    // Calculate difference using same method as backend
    const diffInMs = now.getTime() - utcTime.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInSeconds < 0) return 'Just now';
    if (diffInSeconds < 60) return 'Just now';  // 0-59 seconds
    if (diffInMinutes < 1) return 'Just now';   // Safety check
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;  // 1-59 minutes 
    if (diffInHours < 24) return `${diffInHours}h ago`;     // 1-23 hours
    if (diffInDays <= 30) return `${diffInDays}d ago`;      // 1-30 days
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) return `${diffInMonths}mo ago`;
    
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears}y ago`;
  };

  /*
  const formatFullTimestamp = (timestamp) => {
    const time = new Date(timestamp);
    return time.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };
  */

  const formatUTCTimestamp = (timestamp) => {
    let utcDate;
    
    if (typeof timestamp === 'string') {
      // If it's a string, ensure it's treated as UTC
      if (timestamp.endsWith('Z') || timestamp.includes('+00:00')) {
        // Already has UTC indicator
        utcDate = new Date(timestamp);
      } else {
        // Add Z to ensure UTC parsing
        utcDate = new Date(timestamp + (timestamp.includes('T') ? 'Z' : ''));
      }
    } else if (typeof timestamp === 'number') {
      // Unix timestamp in milliseconds
      utcDate = new Date(timestamp);
    } else {
      // Already a Date object
      utcDate = new Date(timestamp);
    }
    
    
    // CRITICAL: Use UTC methods to avoid any local timezone conversion
    const year = utcDate.getUTCFullYear();
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getUTCDate()).padStart(2, '0');
    const hours = String(utcDate.getUTCHours()).padStart(2, '0');
    const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
    const seconds = String(utcDate.getUTCSeconds()).padStart(2, '0');
    
    const utcString = `${year}-${month}-${day}, ${hours}:${minutes}:${seconds}`;
    
    return utcString;
  };

  // Sessions Tab Content
  const renderSessionsTab = () => (
    <Box>
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>Recent devices</Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search sessions..."
              value={sessionsSearch}
              onChange={(e) => setSessionsSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: sessionsSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSessionsSearch('')}
                      edge="end"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 250 }}
            />
          </Box>
          <Box display="flex" gap={2}>
            <Button
              onClick={handleTerminateAllSessions}
              variant="contained"
              color="error"
              startIcon={<Logout />}
            >
              Log out of all other devices
            </Button>
            <Button
              onClick={loadSessions}
              disabled={sessionsLoading}
              startIcon={<Refresh />}
              variant="outlined"
            >
              Refresh
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          View and manage devices where you're currently logged in. If you've lost one of your devices or notice any suspicious activity, log out of all your devices and take steps to secure your account.
        </Typography>
        <Typography variant="body2" color="primary" sx={{ cursor: 'pointer', mb: 3 }}>
          Learn more about recently used devices.
        </Typography>
      </Box>

      {sessionsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Device</strong></TableCell>
                <TableCell><strong>Application</strong></TableCell>
                <TableCell><strong>IP address</strong></TableCell>
                <TableCell><strong>Last active</strong></TableCell>
                <TableCell align="center"><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <TableRow key={session.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getDeviceIcon(session.device_info)}
                      <Box ml={2}>
                        <Typography variant="body2" fontWeight="medium">
                          {session.device_info?.device_type || 'Computer'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {session.device_info?.os || 'Unknown OS'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {session.device_info?.browser || 'Unknown Browser'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {session.user_agent ? 
                        `v${session.user_agent.match(/Chrome\/(\d+)/) ? 
                          session.user_agent.match(/Chrome\/(\d+)/)[1] : 
                          session.user_agent.match(/Firefox\/(\d+)/) ?
                            session.user_agent.match(/Firefox\/(\d+)/)[1] :
                            session.user_agent.match(/Safari\/(\d+)/) ?
                              session.user_agent.match(/Safari\/(\d+)/)[1] : 'Unknown'
                        }` : ''
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {session.ip_address || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatRelativeTime(session.last_activity)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {session.is_current ? (
                      <Chip 
                        label="Current" 
                        size="small" 
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Button
                        size="small"
                        color="error"
                        variant="text"
                        onClick={() => handleTerminateSession(session)}
                      >
                        Log out
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {sessions.length === 0 && !sessionsLoading && (
        <Alert severity="info">
          No active sessions found.
        </Alert>
      )}
    </Box>
  );

  // Activities Tab Content
  const renderActivitiesTab = () => (
    <Box>
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>Recent Activity</Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <TextField
            size="small"
            placeholder="Search activities..."
            value={activitySearch}
            onChange={(e) => setActivitySearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: activitySearch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setActivitySearch('')}
                    edge="end"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 250 }}
          />
          <Box display="flex" gap={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={activityFilters.activity_type}
                label="Activity Type"
                onChange={(e) => setActivityFilters({
                  ...activityFilters, 
                  activity_type: e.target.value
                })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="login">Login</MenuItem>
                <MenuItem value="logout">Logout</MenuItem>
                <MenuItem value="login_failed">Failed Login</MenuItem>
                <MenuItem value="mfa_setup">MFA Setup</MenuItem>
                <MenuItem value="password_change">Password Change</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Risk Level</InputLabel>
              <Select
                value={activityFilters.risk_level}
                label="Risk Level"
                onChange={(e) => setActivityFilters({
                  ...activityFilters, 
                  risk_level: e.target.value
                })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
            <Button
              onClick={loadActivities}
              disabled={activitiesLoading}
              startIcon={<Refresh />}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      {activitiesLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Activity</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Risk Level</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.map((activity) => (
                <TableRow key={activity.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getActivityIcon(activity.activity_type)}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {activity.activity_type.replace('_', ' ').toUpperCase()}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {activity.description}
                    </Typography>
                    {!activity.success && activity.error_message && (
                      <Typography variant="caption" color="error">
                        {activity.error_message}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={activity.risk_level.toUpperCase()}
                      size="small"
                      color={getRiskColor(activity.risk_level)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {activity.ip_address || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`${formatUTCTimestamp(activity.timestamp)} UTC`}>
                      <Typography variant="body2" color="textSecondary">
                        {formatRelativeTime(activity.timestamp)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {activities.length === 0 && !activitiesLoading && (
        <Alert severity="info">
          No activities found for the selected filters.
        </Alert>
      )}

      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={Math.ceil(activityTotal / itemsPerPage)}
          page={activityPage}
          onChange={(event, value) => setActivityPage(value)}
        />
      </Box>
    </Box>
  );

  // Login History Tab Content
  const renderLoginHistoryTab = () => (
    <Box>
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>Login History</Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <TextField
            size="small"
            placeholder="Search login history..."
            value={loginSearch}
            onChange={(e) => setLoginSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: loginSearch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setLoginSearch('')}
                    edge="end"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 250 }}
          />
          <Box display="flex" gap={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Method</InputLabel>
              <Select
                value={loginFilters.login_method}
                label="Method"
                onChange={(e) => setLoginFilters({
                  ...loginFilters, 
                  login_method: e.target.value
                })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="email_password">Email/Password</MenuItem>
                <MenuItem value="google">Google</MenuItem>
                <MenuItem value="microsoft">Microsoft</MenuItem>
                <MenuItem value="mfa">MFA</MenuItem>
                <MenuItem value="backup_code">Backup Code</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={loginFilters.success_only ?? ''}
                label="Status"
                onChange={(e) => setLoginFilters({
                  ...loginFilters, 
                  success_only: e.target.value === '' ? null : e.target.value === 'true'
                })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Success Only</MenuItem>
                <MenuItem value="false">Failed Only</MenuItem>
              </Select>
            </FormControl>
            <Button
              onClick={loadLoginHistory}
              disabled={loginHistoryLoading}
              startIcon={<Refresh />}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      {loginHistoryLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>MFA</TableCell>
                <TableCell>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loginHistory.map((login) => (
                <TableRow key={login.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {login.success ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Cancel color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {login.success ? 'Success' : 'Failed'}
                      </Typography>
                    </Box>
                    {!login.success && login.failure_reason && (
                      <Typography variant="caption" color="error">
                        {login.failure_reason.replace('_', ' ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {login.login_method.replace('_', ' ').toUpperCase()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {login.ip_address || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {login.mfa_required && (
                      <Chip
                        label={login.mfa_completed ? 'Completed' : 'Required'}
                        size="small"
                        color={login.mfa_completed ? 'success' : 'warning'}
                        icon={<VpnKey />}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`${formatUTCTimestamp(login.timestamp)} UTC`}>
                      <Typography variant="body2" color="textSecondary">
                        {formatRelativeTime(login.timestamp)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {loginHistory.length === 0 && !loginHistoryLoading && (
        <Alert severity="info">
          No login history found for the selected filters.
        </Alert>
      )}

      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={Math.ceil(loginTotal / itemsPerPage)}
          page={loginPage}
          onChange={(event, value) => setLoginPage(value)}
        />
      </Box>
    </Box>
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Activity & Sessions
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Monitor your account activity, manage active sessions, and review login history.
        </Typography>

        <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Active Sessions" />
          <Tab label="Recent Activity" />
          <Tab label="Login History" />
        </Tabs>

        {currentTab === 0 && renderSessionsTab()}
        {currentTab === 1 && renderActivitiesTab()}
        {currentTab === 2 && renderLoginHistoryTab()}
      </Paper>

      {/* Terminate Session Confirmation Dialog */}
      <Dialog 
        open={terminateConfirmOpen} 
        onClose={(event, reason) => {
          if (reason === 'escapeKeyDown') {
            setTerminateConfirmOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Warning color="warning" />
              Terminate Session
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setTerminateConfirmOpen(false)}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to terminate this session? This will log out the device immediately.
          </Typography>
          {sessionToTerminate && (
            <Box mt={2} p={2} bgcolor="background.paper" borderRadius={1} border="1px solid" borderColor="divider">
              <Typography variant="subtitle2">
                {sessionToTerminate.device_info?.browser || 'Unknown Browser'} on {sessionToTerminate.device_info?.os || 'Unknown OS'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {sessionToTerminate.ip_address} • Active since {formatRelativeTime(sessionToTerminate.created_at)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminateConfirmOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmTerminateSession} color="warning" variant="contained">
            Terminate Session
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivitySessions;